import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Locale } from '@/i18n';
import GoogleMapReact from 'google-maps-react-markers';
import {
  type GeoCoder,
  getMapLocale,
  type GoogleMapType,
  isCoordinatesEqual,
  type LatLng,
  type LatLngBoundsLiteral,
  type MapMouseEvent,
  mapOptions
} from '@/utils/maps/mapUtils';
import MapSkeleton from '@/components/skeletons/MapSkeleton';
import useMap from '@/hooks/useMap';
import '@/components/maps/maps.css';

type Props = {
  apiKey: string;
  center: {
    lat: number;
    lng: number;
  };
  zoom?: number;
  onClick: (geocoder: GeoCoder, latlng: LatLng) => void;
  locale: Locale;
  children: ReactNode;
  onChange: (latlng: LatLng, zoom: number, geoCoder: GeoCoder, bounds: LatLngBoundsLiteral) => void;
  manageLocalStates: (source: string, venueId?: string) => void;
};

export const DEFAULT_ZOOM = 13;

export default function GoogleMap({
  apiKey,
  center,
  zoom = DEFAULT_ZOOM,
  onClick,
  locale,
  onChange,
  manageLocalStates,
  children
}: Props) {
  const { mapRef } = useMap();
  const [capturedLatLng, setCapturedLatLng] = useState<LatLng>();
  const [prevCenter, setPrevCenter] = useState(center);
  const [prevZoom, setPrevZoom] = useState(zoom);
  const [geoCoder, setGeocoder] = useState<GeoCoder>();

  const mapLocale = useMemo(() => getMapLocale(locale), [locale]);
  const attachClickEvent = useCallback(
    (map: GoogleMapType, geocoder: GeoCoder) => {
      map.addListener('click', (mapsMouseEvent: MapMouseEvent) => {
        const latlng: LatLng | undefined = mapsMouseEvent.latLng?.toJSON();
        if (latlng) {
          onClick(geocoder, latlng);
          setCapturedLatLng(latlng);
        }
      });
    },
    [onClick]
  );

  const onGoogleApiLoaded = useCallback(
    ({ map }) => {
      mapRef.current = map;
      const mapGeoCoder = new google.maps.Geocoder();
      setGeocoder(mapGeoCoder);
      attachClickEvent(map, mapGeoCoder);
    },
    [attachClickEvent, mapRef]
  );

  /**
   * Trigger on map change, eg. When dragging over the map.
   * We check if the center is changed to initiate the search again
   */
  const onChangeHandler = useCallback(
    async map => {
      const newCenter = { lng: map.center[0], lat: map.center[1] };

      // Added check for 'showVenues' too as we dont need to trigger onChange if we are in intermediate state
      // of getting geo code for full map view.
      if (!isCoordinatesEqual(prevCenter, newCenter) || prevZoom !== map.zoom) {
        onChange(newCenter, map.zoom, geoCoder!, map.bounds.toJSON());
        setPrevCenter(newCenter);
        setPrevZoom(map.zoom);
      }
    },
    [geoCoder, onChange, prevCenter, prevZoom]
  );

  /**
   * Need this useEffect here as click event is attached only once in map.
   * For reading updated state needed the workaround.
   */
  useEffect(() => {
    if (capturedLatLng) {
      manageLocalStates('MAP_CLICK', undefined);
    }
    // Passing function to dependency array will cause infinite rerender
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capturedLatLng]);

  return (
    <GoogleMapReact
      apiKey={apiKey}
      defaultCenter={center}
      defaultZoom={zoom}
      options={mapOptions}
      onGoogleApiLoaded={onGoogleApiLoaded}
      onChange={map => {
        onChangeHandler(map);
      }}
      externalApiParams={mapLocale}
      loadingContent={<MapSkeleton />}
    >
      {children}
    </GoogleMapReact>
  );
}
